import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const MemberAvatarFallbackDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"MemberAvatarFallback"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"member"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"memberId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"FragmentSpread","name":{"kind":"Name","value":"MemberAvatar"}}]}}]}},{"kind":"FragmentDefinition","name":{"kind":"Name","value":"MemberAvatar"},"typeCondition":{"kind":"NamedType","name":{"kind":"Name","value":"Member"}},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"avatarUrl"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"initials"}},{"kind":"Field","name":{"kind":"Name","value":"nonPublic"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"avatarUrl"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"initials"}}]}},{"kind":"Field","name":{"kind":"Name","value":"username"}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"MemberAvatarFallback","document":MemberAvatarFallbackDocument}} as const;
export type MemberAvatarFallbackQueryVariables = Types.Exact<{
  memberId: Types.Scalars['ID']['input'];
}>;


export type MemberAvatarFallbackQuery = (
  { __typename: 'Query' }
  & { member?: Types.Maybe<(
    { __typename: 'Member' }
    & Pick<Types.Member, 'id' | 'avatarUrl' | 'fullName' | 'initials' | 'username'>
    & { nonPublic?: Types.Maybe<(
      { __typename: 'Member_NonPublic' }
      & Pick<Types.Member_NonPublic, 'avatarUrl' | 'fullName' | 'initials'>
    )> }
  )> }
);

/**
 * __useMemberAvatarFallbackQuery__
 *
 * To run a query within a React component, call `useMemberAvatarFallbackQuery` and pass it any options that fit your needs.
 * When your component renders, `useMemberAvatarFallbackQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMemberAvatarFallbackQuery({
 *   variables: {
 *      memberId: // value for 'memberId'
 *   },
 * });
 */
export function useMemberAvatarFallbackQuery(
  baseOptions: TrelloQueryHookOptions<
    MemberAvatarFallbackQuery,
    MemberAvatarFallbackQueryVariables
  >,
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: MemberAvatarFallbackDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    MemberAvatarFallbackQuery,
    MemberAvatarFallbackQueryVariables
  >(MemberAvatarFallbackDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useMemberAvatarFallbackLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    MemberAvatarFallbackQuery,
    MemberAvatarFallbackQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    MemberAvatarFallbackQuery,
    MemberAvatarFallbackQueryVariables
  >(MemberAvatarFallbackDocument, options);
}
export type MemberAvatarFallbackQueryHookResult = ReturnType<
  typeof useMemberAvatarFallbackQuery
>;
export type MemberAvatarFallbackLazyQueryHookResult = ReturnType<
  typeof useMemberAvatarFallbackLazyQuery
>;
export type MemberAvatarFallbackQueryResult = Apollo.QueryResult<
  MemberAvatarFallbackQuery,
  MemberAvatarFallbackQueryVariables
>;
